import { useEffect, useRef, useState } from "react";
import videoAsset from "@/assets/about-person-video.asset.json";
import webmAsset from "@/assets/about-person-webm.asset.json";
import posterAsset from "@/assets/about-person-poster.asset.json";

const SENSITIVITY = 0.8;

/** A paused video whose timeline follows horizontal mouse movement over its frame. */
export function MouseScrubPortrait() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const lastX = useRef<number | null>(null);
  const targetTime = useRef(0);
  const seeking = useRef(false);
  const [canScrub, setCanScrub] = useState(false);
  const [frameReady, setFrameReady] = useState(false);

  useEffect(() => {
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setCanScrub(finePointer.matches && !reducedMotion.matches);
    // Defer the heavy video until the page has loaded and the browser is idle,
    // so it never competes with the poster image and text for bandwidth.
    let idleId: number | undefined;
    const start = () => {
      const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
      idleId = ric ? ric(update, { timeout: 2500 }) : window.setTimeout(update, 1200);
    };
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
    finePointer.addEventListener("change", update);
    reducedMotion.addEventListener("change", update);
    return () => {
      window.removeEventListener("load", start);
      if (idleId !== undefined) {
        const cic = (window as unknown as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
        if (cic) cic(idleId); else window.clearTimeout(idleId);
      }
      finePointer.removeEventListener("change", update);
      reducedMotion.removeEventListener("change", update);
    };
  }, []);

  const seek = () => {
    const video = videoRef.current;
    if (!video || seeking.current || !Number.isFinite(video.duration) || video.duration <= 0) return;
    if (Math.abs(video.currentTime - targetTime.current) < 0.01) return;
    seeking.current = true;
    video.currentTime = targetTime.current;
  };

  const handleMove = (event: React.PointerEvent<HTMLElement>) => {
    if (event.pointerType !== "mouse" || !canScrub) return;
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return;
    if (lastX.current === null) {
      lastX.current = event.clientX;
      return;
    }
    const width = event.currentTarget.getBoundingClientRect().width;
    if (width <= 0) return;
    const delta = event.clientX - lastX.current;
    lastX.current = event.clientX;
    targetTime.current = Math.max(0, Math.min(video.duration - 0.01, targetTime.current + (delta / width) * SENSITIVITY * video.duration));
    seek();
  };

  return (
    <figure
      className="relative aspect-[4/5] overflow-hidden rounded-3xl border border-border bg-card"
      onPointerMove={handleMove}
      onPointerLeave={() => { lastX.current = null; }}
      aria-label="Interactive animated portrait; move your mouse over the image to explore"
    >
      <img
        src={posterAsset.url}
        alt="Animated portrait of a person in a studio"
        width={1928}
        height={1072}
        fetchPriority="high"
        className="absolute inset-0 h-full w-full object-cover"
      />
      {canScrub && (
        <video
          ref={videoRef}
          poster={posterAsset.url}
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
          onLoadedData={() => setFrameReady(true)}
          onSeeked={() => { seeking.current = false; seek(); }}
          onError={() => setFrameReady(false)}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${frameReady ? "opacity-100" : "opacity-0"}`}
        >
          <source src={webmAsset.url} type="video/webm" />
          <source src={videoAsset.url} type="video/mp4" />
        </video>
      )}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/50 via-transparent to-transparent" />
    </figure>
  );
}